import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import isEmpty from "lodash/isEmpty";
import isUndefined from "lodash/isUndefined";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import {
  FeaturedSideBarModel,
  SidebarMenuConfigModel,
} from "../../data/models/SidebarMenuConfigModel";
import { SidebarMenuInjection } from "../../di/SidebarMenuInjection";
import { FeaturedSideBarEntity } from "../entities/FeatureSidebarEntity";
import { IConfigRepository } from "../repositories/IConfigRepository";

@injectable()
export class SidebarMenuUseCase {
  private sidebarRepo: IConfigRepository;

  constructor() {
    this.sidebarRepo = SidebarMenuInjection.getRepo();
  }

  private get cachedUserMenu() {
    return productCache?.getCache(CacheKeyEnum.userMenu);
  }

  private get webLanguage() {
    return productCache?.getCache(CacheKeyEnum.language);
  }

  private fromJsonMenu(
    items: Array<Partial<FeaturedSideBarEntity>> = [],
  ): Array<FeaturedSideBarEntity> {
    return items.map(
      (item) =>
        new FeaturedSideBarEntity({
          ...item,
          name:
            (item?.configuration?.[this.webLanguage] as unknown as string) ||
            item?.name,
          sub: this.fromJsonMenu(item?.sub || []),
        }),
    );
  }

  private keepAllowedFeatures(
    items: Array<FeaturedSideBarEntity> = [],
  ): Array<FeaturedSideBarEntity> {
    return items
      ?.map((item) => {
        let isHasSub = !isEmpty(item?.sub);
        let subs = item?.sub?.filter(
          (ele) => ele?.isHasMainList() || ele?.isHasGeneralList(),
        );
        let isSubAllowed = !isEmpty(subs) && !isUndefined(subs);
        if (isHasSub && isSubAllowed) {
          return new FeaturedSideBarEntity({
            key: item?.key,
            name: item?.name,
            code: item?.code,
            path: item?.path,
            componentName: item?.componentName,
            icon: item?.icon,
            capabilities: item?.capabilities,
            sub: subs || [],
          });
        } else {
          if (item?.isHasMainList() || item?.isHasGeneralList()) {
            return new FeaturedSideBarEntity({
              key: item?.key,
              name: item?.name,
              code: item?.code,
              path: item?.path,
              componentName: item?.componentName,
              icon: item?.icon,
              capabilities: item?.capabilities,
              sub: item?.sub || [],
            });
          } else {
            return undefined;
          }
        }
      })
      ?.filter((ele) => !isUndefined(ele) && Boolean);
  }

  async getSidebarMenu(): Promise<
    BaseApiResponseModel<SidebarMenuConfigModel>
  > {
    return await this.getSidebarMenuFromSource(this.cachedUserMenu || []);
  }

  async getSidebarMenuFromSource(
    menuSource: FeaturedSideBarModel[] = [],
  ): Promise<BaseApiResponseModel<SidebarMenuConfigModel>> {
    let menuConfig: FeaturedSideBarModel[] = [];
    let rawMenu: FeaturedSideBarModel[] = [];

    switch (this.webLanguage) {
      case "vi":
        rawMenu = this.fromJsonMenu(menuSource);
        break;
      case "en":
        rawMenu = this.fromJsonMenu(menuSource);
        break;
      default:
        rawMenu = this.fromJsonMenu(menuSource);
    }

    menuConfig = this.keepAllowedFeatures(rawMenu);
    return await this.sidebarRepo.getSidebarMenu(menuConfig);
  }
}
